import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  AssetMaster,
  CatalogService,
  HospitalityProperty,
  HospitalityRoomUnit,
  RoomMaintenanceRequest
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-hospitality-room-maintenance',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule,
    TableModule, ButtonModule, SelectModule, InputTextModule, TextareaModule, DialogModule, CheckboxModule
  ],
  template: `
    <ain-page [title]="t('catalog.hospitality.maintenance.title')" [description]="t('catalog.hospitality.maintenance.description')">
      @if (loading() && rows().length === 0) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <p-select
              [options]="propertyOptions()"
              [(ngModel)]="selectedPropertyId"
              (ngModelChange)="onPropertyChange()"
              optionLabel="label"
              optionValue="value"
              [placeholder]="t('catalog.hospitality.property')"
              [filter]="true"
              appendTo="body" />
            <p-select
              [options]="statusOptions"
              [(ngModel)]="statusFilter"
              (ngModelChange)="load()"
              optionLabel="label"
              optionValue="value"
              [placeholder]="t('catalog.tracking.status')"
              appendTo="body" />
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
            <button pButton type="button" [label]="t('common.create')" [disabled]="!selectedPropertyId || rooms().length === 0" (click)="openCreateDialog()"></button>
            <a routerLink="/cmms/work-orders">{{ t('catalog.cmms.workOrders.title') }}</a>
          </div>

          @if (errorMessage()) {
            <p class="message error">{{ errorMessage() }}</p>
          }

          <p-table [value]="rows()" [paginator]="true" [rows]="20">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.hospitality.roomNumber') }}</th>
                <th>{{ t('catalog.hospitality.maintenance.issue') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.hospitality.maintenance.workOrder') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.roomNumber }}</td>
                <td>{{ row.issueDescription }}</td>
                <td>{{ row.status }}</td>
                <td>
                  @if (row.workOrderId) {
                    <a [routerLink]="['/cmms/work-orders', row.workOrderId]">{{ row.workOrderId }}</a>
                  } @else {
                    —
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }

      <p-dialog
        [header]="t('catalog.hospitality.maintenance.createTitle')"
        [visible]="createDialogOpen()"
        [modal]="true"
        [style]="{ width: '36rem', maxWidth: '95vw' }"
        (visibleChange)="onCreateDialogVisibleChange($event)">
        <div class="form-grid">
          <label class="full">
            <span>{{ t('catalog.hospitality.roomNumber') }}</span>
            <p-select [options]="roomOptions()" [(ngModel)]="createForm.roomUnitId" optionLabel="label" optionValue="value" appendTo="body" />
          </label>
          <label class="full">
            <span>{{ t('catalog.hospitality.maintenance.issue') }}</span>
            <textarea pTextarea rows="3" [(ngModel)]="createForm.issueDescription"></textarea>
          </label>
          <label class="checkbox-row full">
            <p-checkbox [(ngModel)]="createForm.createWorkOrder" [binary]="true" />
            <span>{{ t('catalog.hospitality.maintenance.createWorkOrder') }}</span>
          </label>
          @if (createForm.createWorkOrder) {
            <label class="full">
              <span>{{ t('catalog.hospitality.maintenance.asset') }}</span>
              <p-select
                [options]="assetOptions()"
                [(ngModel)]="createForm.assetId"
                optionLabel="label"
                optionValue="value"
                [showClear]="true"
                [placeholder]="t('catalog.hospitality.maintenance.assetAuto')"
                appendTo="body" />
            </label>
          }
        </div>
        <div class="dialog-footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeCreateDialog()"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveRequest()"></button>
        </div>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 12px; }
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .toolbar a { color: #1d4ed8; font-weight: 600; text-decoration: none; margin-inline-start: auto; }
    .message.error { color: #b91c1c; margin: 0; }
    .form-grid { display: grid; gap: 10px; }
    .form-grid label { display: grid; gap: 4px; font-size: 13px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
  `]
})
export class HospitalityRoomMaintenanceComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly rows = signal<RoomMaintenanceRequest[]>([]);
  readonly properties = signal<HospitalityProperty[]>([]);
  readonly rooms = signal<HospitalityRoomUnit[]>([]);
  readonly assets = signal<AssetMaster[]>([]);
  readonly createDialogOpen = signal(false);
  readonly errorMessage = signal('');

  selectedPropertyId = '';
  statusFilter = '';

  createForm = {
    roomUnitId: '',
    issueDescription: '',
    createWorkOrder: true,
    assetId: ''
  };

  readonly statusOptions = [
    { label: 'All', value: '' },
    { label: 'Pending', value: 'Pending' },
    { label: 'WorkOrderCreated', value: 'WorkOrderCreated' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  readonly propertyOptions = computed(() =>
    this.properties().map(item => ({
      value: item.id,
      label: item.propertyCode ? `${item.propertyCode} — ${item.name}` : item.name
    })));

  readonly roomOptions = computed(() =>
    this.rooms().map(item => ({
      value: item.id,
      label: item.roomNumber
    })));

  readonly assetOptions = computed(() =>
    this.assets().map(item => ({
      value: item.id,
      label: item.assetNumber ? `${item.assetNumber} — ${item.name}` : item.name
    })));

  ngOnInit(): void {
    this.service.listHospitalityProperties().subscribe({
      next: rows => {
        this.properties.set(rows);
        if (rows.length === 1) {
          this.selectedPropertyId = rows[0].id;
          this.onPropertyChange();
        } else {
          this.load();
        }
      },
      error: () => this.loading.set(false)
    });

    this.service.listAssets().subscribe({
      next: rows => this.assets.set(rows),
      error: () => this.assets.set([])
    });
  }

  onPropertyChange(): void {
    if (!this.selectedPropertyId) {
      this.rooms.set([]);
      this.rows.set([]);
      this.loading.set(false);
      return;
    }

    this.service.listHospitalityRooms(this.selectedPropertyId).subscribe({
      next: rooms => {
        this.rooms.set(rooms);
        this.loadRequests(rooms.map(item => item.id));
      },
      error: () => {
        this.rooms.set([]);
        this.loadRequests([]);
      }
    });
  }

  load(): void {
    if (this.selectedPropertyId && this.rooms().length > 0) {
      this.loadRequests(this.rooms().map(item => item.id));
      return;
    }

    if (this.selectedPropertyId) {
      this.onPropertyChange();
      return;
    }

    this.loadRequests();
  }

  private loadRequests(roomIds?: string[]): void {
    this.loading.set(true);
    this.service.listRoomMaintenanceRequests(this.statusFilter || undefined).subscribe({
      next: rows => {
        const filtered = roomIds?.length
          ? rows.filter(row => roomIds.includes(row.roomUnitId))
          : rows;
        this.rows.set(filtered);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  openCreateDialog(): void {
    this.createForm = {
      roomUnitId: this.rooms()[0]?.id ?? '',
      issueDescription: '',
      createWorkOrder: true,
      assetId: ''
    };
    this.errorMessage.set('');
    this.createDialogOpen.set(true);
  }

  onCreateDialogVisibleChange(visible: boolean): void {
    if (!visible) this.closeCreateDialog();
  }

  closeCreateDialog(): void {
    this.createDialogOpen.set(false);
  }

  saveRequest(): void {
    if (!this.createForm.roomUnitId || !this.createForm.issueDescription.trim()) {
      return;
    }

    this.saving.set(true);
    this.errorMessage.set('');
    this.service.createRoomMaintenanceRequest({
      roomUnitId: this.createForm.roomUnitId,
      issueDescription: this.createForm.issueDescription.trim(),
      createWorkOrder: this.createForm.createWorkOrder,
      assetId: this.createForm.assetId || null
    }).subscribe({
      next: () => {
        this.saving.set(false);
        this.closeCreateDialog();
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.errorMessage.set(this.t('catalog.hospitality.maintenance.saveFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
